import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getLiveActivity } from '../api';
import { useEnv } from '../contexts/EnvContext';

const REFRESH_MS = 60000;

const LiveActivityContext = createContext({ data: null, error: null, refresh: () => {} });

/**
 * Open connections and unpaid cash-out requests, refreshed every minute and
 * whenever the PROD/DEV switch flips.
 *
 * One poll for the whole app, so the nav badge, the dashboard tiles and the
 * Connections page always show the same number.
 *
 * `error` is set when the request itself failed. Each half of `data` also
 * carries its own `ok`, because the server reports a half it could not read
 * rather than sending zeros: show "unavailable" for it, never 0.
 */
export function LiveActivityProvider({ enabled = true, children }) {
  const { env } = useEnv();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const alive = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const next = await getLiveActivity();
      if (!alive.current) return;
      setData(next);
      setError(null);
    } catch (e) {
      if (!alive.current) return;
      setError(e?.response?.data?.error || e?.message || 'Could not load live activity');
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    if (!enabled) {
      setData(null);
      setError(null);
      return undefined;
    }
    refresh();
    const id = setInterval(refresh, REFRESH_MS);
    return () => { alive.current = false; clearInterval(id); };
  }, [enabled, refresh, env]);

  const value = useMemo(() => ({ data, error, refresh }), [data, error, refresh]);
  return <LiveActivityContext.Provider value={value}>{children}</LiveActivityContext.Provider>;
}

export const useLiveActivity = () => useContext(LiveActivityContext);
